import { useMemo } from 'react';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';
import type { StructuredPartyActionError } from 'types/api-payloads';
import { actionErrorMessage } from 'utils/api-errors';
import type {
  AutoMatchConfiguration,
  AutoMatchConfigurationInput,
  AutoMatchDecisionDetail,
  AutoMatchDecisionFilters,
  AutoMatchDecisionsResponse,
  CountyOption,
} from './types';
import { reasonLabels } from './utils';

// The configuration and the county lists are loaded once and kept for the
// session: none of them change while the page is open except through this
// page's own save.
const ONCE = { staleTime: Infinity } as const;

export const configQueryOptions = queryOptions({
  queryKey: ['auto-match', 'configuration'],
  queryFn: ({ signal }) =>
    api
      .get('admin/auto_match_configuration', { signal })
      .json<AutoMatchConfiguration>(),
  ...ONCE,
});

// All counties, for the enabled-counties picker (the same list the AFE catalog
// loads its county options from).
export const countiesQueryOptions = queryOptions({
  queryKey: ['auto-match', 'counties'],
  queryFn: ({ signal }) =>
    api
      .get('counties', { signal })
      .json<{ results: CountyOption[] }>()
      .then((body) => body.results),
  ...ONCE,
});

// The counties the decision log has rows for — its own query, so the County
// filter stays stable across the paginated list's refetches.
export const decisionCountiesQueryOptions = queryOptions({
  queryKey: ['auto-match', 'decision-counties'],
  queryFn: ({ signal }) =>
    api
      .get('admin/auto_match_decisions/counties', { signal })
      .json<{ results: CountyOption[] }>()
      .then((body) => body.results),
  ...ONCE,
});

// Column ids are camelCase; the finder's sort_by whitelist is snake_case like
// every other param. Only these two ids differ from their param.
const SORT_PARAM: Record<string, string> = {
  createdAt: 'created_at',
  documentId: 'document_id',
};

/**
 * The decisions endpoint's query string, leaving out empty filters.
 * @param {AutoMatchDecisionFilters} filters - The log's filter, sort and page state
 * @return {URLSearchParams} The search params
 */
export function decisionsParams(
  filters: AutoMatchDecisionFilters
): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.outcome) params.append('outcome', filters.outcome);
  if (filters.suppressionReason)
    params.append('suppression_reason', filters.suppressionReason);
  if (filters.reversal) params.append('reversal', filters.reversal);
  filters.countyIds.forEach((id) =>
    params.append('county_ids[]', id.toString())
  );
  if (filters.after) params.append('after', filters.after);
  if (filters.before) params.append('before', filters.before);
  if (filters.search) params.append('search', filters.search);
  params.append('sort_by', SORT_PARAM[filters.sortBy] ?? filters.sortBy);
  params.append('sort_dir', filters.sortDir);
  params.append('page', filters.page.toString());
  params.append('per_page', filters.perPage.toString());
  return params;
}

/**
 * One page of the decision log for a filter state.
 * @param {AutoMatchDecisionFilters} filters - The log's filter, sort and page state
 * @return The queryOptions for that page
 */
export const decisionsQueryOptions = (filters: AutoMatchDecisionFilters) =>
  queryOptions({
    queryKey: ['auto-match', 'decisions', filters],
    queryFn: ({ signal }) =>
      api
        .get('admin/auto_match_decisions', {
          searchParams: decisionsParams(filters),
          signal,
        })
        .json<AutoMatchDecisionsResponse>(),
    // Holds the current page on screen while the next one loads.
    placeholderData: keepPreviousData,
  });

/**
 * One decision's full detail, fetched when its row expands.
 * @param {number} id - The decision's id
 * @return The queryOptions for that decision
 */
export const decisionDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: ['auto-match', 'decision', id],
    queryFn: ({ signal }) =>
      api
        .get(`admin/auto_match_decisions/${id}`, { signal })
        .json<AutoMatchDecisionDetail>(),
  });

/**
 * The active auto-match configuration.
 * @return The configuration query
 */
export const useAutoMatchConfig = () => useQuery(configQueryOptions);

/**
 * Every county, for the enabled-counties picker.
 * @return The counties query
 */
export const useCounties = () => useQuery(countiesQueryOptions);

/**
 * The counties the decision log has rows for.
 * @return The decision-counties query
 */
export const useDecisionCounties = () => useQuery(decisionCountiesQueryOptions);

/**
 * One page of the decision log.
 * @param {AutoMatchDecisionFilters} filters - The log's filter, sort and page state
 * @return The decisions query
 */
export const useDecisions = (filters: AutoMatchDecisionFilters) =>
  useQuery(decisionsQueryOptions(filters));

/**
 * One decision's full detail.
 * @param {number} id - The decision's id
 * @return The decision query
 */
export const useDecisionDetail = (id: number) =>
  useQuery(decisionDetailQueryOptions(id));

/**
 * Turns a failed save into one readable message: every message of a
 * field-keyed 422 joined into one sentence, a message about the whole
 * configuration, an application error list, or a single error string.
 * @param {unknown} error - What the PUT rejected with
 * @return {Promise<Error>} The error to surface
 */
async function saveError(error: unknown): Promise<Error> {
  if (!(error instanceof HTTPError)) {
    return error instanceof Error ? error : new Error('Save failed');
  }
  const body = (await error.response
    .json()
    .catch(() => null)) as StructuredPartyActionError | null;
  return new Error(
    actionErrorMessage(body ?? undefined, 'Save failed', { all: true })
  );
}

/**
 * Saves the whole configuration atomically with its rationale, and caches the
 * server's new active configuration (with its incremented version).
 * @return The save mutation
 */
export const useSaveConfiguration = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AutoMatchConfigurationInput) =>
      api
        .put('admin/auto_match_configuration', { json: input })
        .json<AutoMatchConfiguration>()
        .catch(async (error: unknown) => {
          throw await saveError(error);
        }),
    onSuccess: (updated) =>
      queryClient.setQueryData(configQueryOptions.queryKey, updated),
  });
};

/**
 * The reason labels, guardrail names read from the active configuration.
 * @return {Record<string, string>} Labels by basis or suppression-reason token
 */
export const useReasonLabels = () => {
  const { data } = useAutoMatchConfig();
  return useMemo(
    () => reasonLabels(data?.availableGuardrails),
    [data?.availableGuardrails]
  );
};
